"use client";

import { useState, useEffect, useCallback } from "react";
import { ontologyListViews } from "../sdk";
import type { ViewResponse } from "../sdk";

// Re-export SDK types for consumers
export type { ViewResponse };

interface UseViewsResult {
  views: ViewResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing lakehouse views
 * @param orgId - Organization ID
 * @returns Views management state and operations
 */
export function useViews(orgId: string): UseViewsResult {
  const [views, setViews] = useState<ViewResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchViews = useCallback(async () => {
    if (!orgId) {
      setViews([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ontologyListViews({
        query: {
          org_id: orgId,
          limit: 100,
          offset: 0,
        },
      });
      setViews(response.data?.views || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch views");
      setViews([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const refetch = useCallback(async () => {
    await fetchViews();
  }, [fetchViews]);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  return {
    views,
    loading,
    error,
    refetch,
  };
}
